import React, { useMemo } from 'react';
import { AxisOptions, Chart } from 'react-charts';
import Wallet from '../assets/icons/Wallet';
import Copy from '../assets/icons/Copy';
import { cn, copyToClipboard } from '../utils';
import { getCurrentUser } from '../api/user';
import { useQuery } from '@tanstack/react-query';
import { getWalletInfo } from '../api/wallets';

const mock_data = [
  {
    label: 'Wallet Balance',
    data: [
      { primary: new Date(2025, 0, 1), secondary: 100 },
      { primary: new Date(2025, 0, 2), secondary: 120 },
      { primary: new Date(2025, 0, 3), secondary: 90 },
      { primary: new Date(2025, 0, 4), secondary: 140 },
      { primary: new Date(2025, 0, 5), secondary: 130 },
    ],
  },
];

const calculatePriceChange = (oldPrice: number, newPrice: number) => {
  if (oldPrice === 0) {
    return newPrice > 0 ? 100 : 0;
  }

  const changePercent = ((newPrice - oldPrice) / oldPrice) * 100;
  return Number(changePercent.toFixed(2));
};

const WalletGraph = () => {
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
    staleTime: 1000 * 30,
  });

  const { data: walletInfo } = useQuery({
    queryKey: ['wallet-info'],
    queryFn: getWalletInfo,
    staleTime: 1000 * 30,
  });

  const data = React.useMemo(() => {
    if (!walletInfo) return mock_data;

    const items = walletInfo.price_history.map((item) => ({
      primary: new Date(item[0]),
      secondary: item[1],
    }));

    return [
      {
        label: 'Wallet Balance',
        data: items,
      },
    ];
  }, [walletInfo]);

  const finacialData = useMemo(() => {
    if (!walletInfo)
      return {
        percent: 0,
        history: [],
        first: 0,
        last: 0,
      };

    const history = walletInfo?.price_history || [];
    const length = history.length;
    const first = history[0][1];
    const last = history[length - 1][1];

    return {
      percent: calculatePriceChange(first, last),
      history,
      first,
      last,
    };
  }, [walletInfo]);

  const primaryAxis = React.useMemo<
    AxisOptions<(typeof data)[number]['data'][number]>
      >(
      () => ({
        getValue: (datum) => datum.primary,
        scaleType: 'time',
        min: new Date(data[0].data[0].primary), // first date
      }),
      [data],
      );

  const minPrice = useMemo(() => {
    if (!walletInfo) return 0;

    return Math.min(...walletInfo.price_history.map(p => p[1]));
  }, [walletInfo]);

  const secondaryAxes = React.useMemo<
    AxisOptions<(typeof data)[number]['data'][number]>[]
      >(
      () => [
        {
          getValue: (datum) => datum.secondary,
          elementType: 'area',
          hardMin: minPrice * 0.98,
        },
      ],
      [],
      );

  const address = user?.walletAddress || '';

  return (
    <div>
      <div className='mb-3'>
        <span className='text-lg font-medium text-white'>Your Wallet:</span>

        <div className='grid grid-cols-2 grid-rows-2'>
          <div className='flex items-center text-sm font-medium text-teal'>
            Your current balance is:
          </div>

          <div className='text-end text-lg font-medium text-white'>{`${
            walletInfo?.balance || 0
          } DAAC`}</div>

          <div className='flex items-center gap-1 text-sm font-medium text-teal'>
            <Wallet />

            {`${address.slice(0, 5)} ... ${address.slice(-5)}`}

            <button onClick={() => copyToClipboard(address)}>
              <Copy />
            </button>
          </div>

          <div className='flex items-center justify-end gap-1'>
            <div className='text-sm font-medium text-white'>{`US$${(
              finacialData.last * (walletInfo?.balance || 0)
            ).toFixed(2)}`}</div>

            <div className='h-[10px] border-r-2 border-dashed border-[#2D6271]' />

            <div
              className={cn(
                'flex items-center gap-1 text-sm font-medium',
                finacialData.percent > 0 ? 'text-neon-green' : 'text-rose-600',
              )}
            >
              {finacialData.percent}%

              <i
                className={cn(
                  'fa-solid',
                  finacialData.percent > 0 ? 'fa-arrow-up' : 'fa-arrow-down',
                )}
              ></i>
            </div>
          </div>
        </div>
      </div>

      <div className='h-20 w-full'>
        <Chart
          options={{
            data,
            primaryAxis,
            secondaryAxes,
            getSeriesStyle: () => ({
              color: '#91F800',
              area: { fill: '#91F80080' },
            }),
            tooltip: false,
            primaryCursor: false,
            secondaryCursor: false,
          }}
        />
      </div>
    </div>
  );
};

export default WalletGraph;
